import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../../data/define';

// api 인스턴트
const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: (status) => status >= 200 && status < 300,
});

// api 요청 인터셉터
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      if (config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// 리프레쉬용 인스턴스
const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// 리프레쉬 토큰 요청 인터셉터
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalReq = error.config;
    if (!originalReq || originalReq.url?.endsWith('/user/refresh-token')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const refToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refToken) {
        // 애초에 로그인한 적이 없는 상태 — 재발급을 시도할 필요도, 리다이렉트할 필요도 없다.
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        return Promise.reject(error);
      }
      try {
        const { data } = await refreshClient.post(`/user/refresh-token`, {
          refreshToken: refToken,
        });
        const { accessToken } = data as { accessToken: string };
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        if (originalReq.headers) {
          originalReq.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return axiosInstance(originalReq);
      } catch (refreshError) {
        console.error('Token refresh failed:', {
          status: (refreshError as AxiosError).response?.status,
          data: (refreshError as AxiosError).response?.data,
        });
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
